import { useEffect, useState } from "react";
import axios from "axios";
import { useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
let _configCache = null;
let _configFetchedAt = 0;

async function fetchPublicConfig() {
  const now = Date.now();
  if (_configCache && now - _configFetchedAt < 60_000) return _configCache;
  try {
    const { data } = await axios.get(`${BACKEND_URL}/api/public-config`);
    _configCache = data;
    _configFetchedAt = now;
    return data;
  } catch {
    return { seo: {}, adsense: { enabled: false } };
  }
}

// Routes where we NEVER show ads (paid features + admin/checkout)
const AD_EXCLUDE = [
  "/creator-os",
  "/admin",
  "/partner",
  "/studio",
  "/payment",
  "/signup",
  "/login",
  "/dashboard",
  "/my-invoices",
];

function shouldShowAds(pathname, user) {
  // Never inside paid areas
  if (AD_EXCLUDE.some((p) => pathname.startsWith(p))) return false;
  // Never for paying/admin users (better UX for paid customers)
  if (user?.is_admin) return false;
  if (user?.plan === "creator") return false;
  return true;
}

export default function AdSense() {
  const { user } = useAuth();
  const loc = useLocation();
  const [cfg, setCfg] = useState(null);

  useEffect(() => {
    fetchPublicConfig().then(setCfg);
  }, []);

  useEffect(() => {
    if (!cfg?.adsense?.enabled || !cfg.adsense.publisher_id) return;
    if (!shouldShowAds(loc.pathname, user)) return;
    if (document.getElementById("adsbygoogle-js")) return;

    const s = document.createElement("script");
    s.id = "adsbygoogle-js";
    s.async = true;
    s.crossOrigin = "anonymous";
    s.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${cfg.adsense.publisher_id}`;
    document.head.appendChild(s);
  }, [cfg, loc.pathname, user]);

  return null;
}

export function AdSlot({ slotId: propSlotId, format = "auto", placement = "landing", testId = "adslot" }) {
  const { user } = useAuth();
  const loc = useLocation();
  const [cfg, setCfg] = useState(null);

  useEffect(() => {
    fetchPublicConfig().then(setCfg);
  }, []);

  useEffect(() => {
    if (!cfg?.adsense?.enabled) return;
    if (!shouldShowAds(loc.pathname, user)) return;
    try {
      // eslint-disable-next-line no-undef
      (window.adsbygoogle = window.adsbygoogle || []).push({});
    } catch (e) { /* ignore */ }
  }, [cfg, loc.pathname, user]);

  if (!cfg?.adsense?.enabled || !cfg.adsense.publisher_id) return null;
  if (!shouldShowAds(loc.pathname, user)) return null;

  const slotId =
    propSlotId ||
    (placement === "policy" ? cfg.adsense.policy_slot_id : cfg.adsense.landing_slot_id);
  if (!slotId) return null;

  return (
    <div className="my-8 mx-auto max-w-3xl" data-testid={testId}>
      <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1 text-center">
        Advertisement
      </div>
      <ins
        className="adsbygoogle"
        style={{ display: "block" }}
        data-ad-client={cfg.adsense.publisher_id}
        data-ad-slot={slotId}
        data-ad-format={format}
        data-full-width-responsive="true"
      />
    </div>
  );
}
